import { Link } from "react-router-dom";
import { Aperture, ArrowLeft } from "lucide-react";

const EFFECTIVE_DATE = "June 25, 2026";
const COMPANY = "Chapter 8 Studio";
const CONTACT_EMAIL = "lawrence@chapter8studio.com";

function Section({ title, children }) {
  return (
    <section className="mt-10">
      <h2 className="font-display text-2xl text-white mb-3">{title}</h2>
      <div className="space-y-3 text-neutral-300 leading-relaxed text-[15px]">{children}</div>
    </section>
  );
}

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background">
      <div className="border-b border-white/10 bg-black/60 backdrop-blur-2xl">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="privacy-logo" className="flex items-center gap-2.5">
            <Aperture className="h-6 w-6 text-amber-glow" strokeWidth={1.5} />
            <span className="font-display text-xl tracking-tight">Aevolux</span>
          </Link>
          <Link to="/login" data-testid="privacy-back" className="flex items-center gap-2 text-sm text-neutral-400 hover:text-white transition-colors">
            <ArrowLeft className="h-4 w-4" /> Back
          </Link>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-16" data-testid="privacy-content">
        <p className="text-xs tracking-[0.3em] uppercase font-bold text-amber-glow mb-3">Legal</p>
        <h1 className="font-display text-5xl tracking-tight">Privacy Policy</h1>
        <p className="text-neutral-500 mt-4">Effective date: {EFFECTIVE_DATE}</p>

        <p className="mt-8 text-neutral-300 leading-relaxed text-[15px]">
          This Privacy Policy explains how {COMPANY} ("we", "us", "our") collects, uses, and protects your
          information when you use Aevolux (the "Service"), an AI assistant that generates photography lens
          and lighting suggestions. By using the Service you agree to the practices described below.
        </p>

        <Section title="1. Information We Collect">
          <p><strong className="text-white">Account information.</strong> When you create an account we collect your name, email address, and a password. Passwords are never stored in plain text — they are stored only as a one-way hash (bcrypt).</p>
          <p><strong className="text-white">Content you create.</strong> The lens, lighting, and light-count selections you submit, the AI-generated shoot suggestions, and any presets you save are stored in your account so you can revisit them.</p>
          <p><strong className="text-white">Technical data.</strong> We process limited technical information such as login attempts (for security/abuse prevention) and standard server logs.</p>
        </Section>

        <Section title="2. How We Use Your Information">
          <ul className="list-disc pl-5 space-y-2">
            <li>To create and authenticate your account and keep it secure.</li>
            <li>To generate, store, and display your photography suggestions and presets.</li>
            <li>To send transactional emails, such as password-reset links.</li>
            <li>To protect the Service against fraud, abuse, and excessive use (e.g., rate limiting).</li>
          </ul>
        </Section>

        <Section title="3. Third-Party Services">
          <p>We rely on a small number of trusted processors to operate the Service:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li><strong className="text-white">AI provider (Anthropic Claude).</strong> Your lens/lighting selections are sent to the AI model to generate suggestions. We do not send your email or password to the AI provider.</li>
            <li><strong className="text-white">Resend.</strong> Used to deliver transactional emails such as password resets.</li>
            <li><strong className="text-white">Database hosting.</strong> Your account data and saved content are stored in our managed database.</li>
          </ul>
          <p>These providers process data only on our behalf and according to their own privacy and security commitments.</p>
        </Section>

        <Section title="4. Data Retention">
          <p>We retain your account information and saved content for as long as your account is active. Password-reset tokens expire within one hour. You may request deletion of your account and associated data at any time (see Contact below).</p>
        </Section>

        <Section title="5. Security">
          <p>We use industry-standard safeguards including hashed passwords, signed authentication tokens, encrypted transport (HTTPS), brute-force lockout, and request rate limiting. No method of transmission or storage is 100% secure, but we work to protect your information.</p>
        </Section>

        <Section title="6. Your Rights">
          <p>Depending on your location, you may have the right to access, correct, export, or delete your personal data, and to object to or restrict certain processing. To exercise these rights, contact us using the details below.</p>
          <p><strong className="text-white">Illinois &amp; Arizona residents.</strong> {COMPANY} operates from Illinois and Arizona. We do not collect, capture, or store biometric identifiers or biometric information (such as facial geometry, fingerprints, or voiceprints) as defined by the Illinois Biometric Information Privacy Act (BIPA). Residents of Illinois and Arizona may contact us at any time to access or delete the personal information associated with their account.</p>
        </Section>

        <Section title="7. Governing Law">
          <p>This Privacy Policy and any dispute arising from your use of the Service are governed by the laws of the State of Illinois and, where applicable, the State of Arizona, without regard to conflict-of-law principles.</p>
        </Section>

        <Section title="8. Children's Privacy">
          <p>The Service is not directed to children under 13 (or the minimum age required in your jurisdiction), and we do not knowingly collect personal data from them.</p>
        </Section>

        <Section title="9. Changes to This Policy">
          <p>We may update this Privacy Policy from time to time. Material changes will be reflected by updating the effective date above. Continued use of the Service after changes take effect constitutes acceptance.</p>
        </Section>

        <Section title="10. Contact Us">
          <p>If you have questions about this Privacy Policy or your data, contact {COMPANY} at{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} data-testid="privacy-contact-email" className="text-amber-glow hover:underline">{CONTACT_EMAIL}</a>.
          </p>
        </Section>

        <div className="mt-16 pt-8 border-t border-white/10 text-sm text-neutral-500">
          © {new Date().getFullYear()} {COMPANY}. All rights reserved.
        </div>
      </div>
    </div>
  );
}
